<template>
  <SidebarTemplate :sidebar="sidebar" :loading="false" :uuid="uuid">
    <template slot="header-content">
      <Select
        class="w-full"
        :options="currentSectionStoreys"
        button-class="px-1"
        :chevron-size="18"
        :y-offset="10"
        :value="currentStorey"
        @input="changeStorey"
      >
        <template v-if="$windowSize.isMobile" #title>
          {{ $t('titles.Storey') }}
        </template>
        <template #current-option="{ option }">
          {{
            option.translate && option.translate.name
              ? option.translate.name
              : $t('titles.Storey') +
                ' ' +
                $t('titles.Number symbol') +
                option?.number
          }}
          <span v-if="currentSectionStoreys.length > 1">
            / {{ currentSectionStoreys.length }}
          </span>
        </template>
        <template #option="{ option }">
          <span v-if="option.translate && option.translate.name">
            {{ option.translate.name }}
          </span>
          <span v-else>
            {{ $t('titles.Storey') }}&nbsp;{{ $t('titles.Number symbol')
            }}{{ option?.number }}
          </span>
        </template>
      </Select>
    </template>
    <SkeletonLoader v-if="loadingState.axios">
      <div
        class="mb-5 bg-gray-200 dark:bg-neutral-800"
        :class="{
          '!h-70': sidebar.size == 1 || $windowSize.isMobile,
          '!h-90': sidebar.size == 2 && !$windowSize.isMobile,
          '!h-130': sidebar.size == 3 && !$windowSize.isMobile,
        }"
      ></div>
    </SkeletonLoader>
    <div
      v-if="frame?.image && !canvasHasError && !loadingState.axios"
      class="w-full"
    >
      <div ref="canvasContainer" class="mb-2 w-full">
        <InteractiveCanvas
          :class="{
            '!h-70': sidebar.size == 1 || $windowSize.isMobile,
            '!h-90': sidebar.size == 2 && !$windowSize.isMobile,
            '!h-130': sidebar.size == 3 && !$windowSize.isMobile,
          }"
          class="storey-interactive"
          :frame="frame"
          stretch-mode="contain"
          @frameclick="handleFrameClick"
          @layermouseover="handleLayerMouseOver"
          @layermouseleave="handleLayerMouseLeave"
          @loaded="interactiveCanvasLoaded"
          @mounted="interactiveCanvasMounted"
          @error="interactiveCanvasError"
        >
          <template #canvasLayers="{ aspectRatio, items, zoom, imageSize }">
            <CanvasLayers
              :dragging-state="false"
              :aspect-ratio="aspectRatio"
              :items="items"
              :breakpoint="7"
              :image-size="imageSize"
              :extra-scale="1"
              :zoom="zoom"
            />
          </template>
        </InteractiveCanvas>
      </div>
      <router-link
        class="hover:text-primary flex items-end justify-center text-sm leading-[1] text-gray-500 dark:text-neutral-300"
        :to="
          getRouterConfig({
            name: 'Interactive',
            params: {
              interactiveId: interactive.id,
              frameId: interactive.main_frame_id,
              storeyId:
                currentStorey.type == 'group'
                  ? sidebar?.context
                  : sidebar?.subcontext,
            },
          })
        "
      >
        <lucide-icon icon="Interactive" size="14" class="mb-0.5 me-1" />
        {{ $t('control.Open storey interactive') }}
      </router-link>
    </div>
    <InstancesList
      class="mt-3 px-2"
      :highlighted="highlightedId"
      :instances="itemsToDisplay"
      @instanceOver="handleInstanceMouseOver"
      @instanceLeave="handleInstanceMouseLeave"
    >
    </InstancesList>
  </SidebarTemplate>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SidebarTemplate from '@/components/sidebar/SidebarTemplate.vue';
import {
  InteractiveCanvas,
  PolygonTool,
  CanvasLayers,
  LabelTool,
} from '@dmlnc/hello_world';
import Select from '@/components/ui/Select';
import InstancesList from '../components/InstancesList';
import SkeletonLoader from '@/components/ui/SkeletonLoader';

export default {
  name: 'StoreyTemplate',
  components: {
    InstancesList,
    SidebarTemplate,
    InteractiveCanvas,
    CanvasLayers,
    Select,
    SkeletonLoader,
  },
  props: {
    sidebar: {
      type: Object,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    currentStorey: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      frame: null,

      loadingState: { axios: true, canvas: true },
      canvasHasError: false,

      highlightedId: null,
      interactive: null,
      canvas: null,
      abortController: null,
    };
  },
  computed: {
    ...mapGetters([
      'instances',
      'storeys',
      'interactiveSettings',
      'visibleSidebars',
    ]),
    loading() {
      return this.loadingState.axios || this.loadingState.canvas;
    },

    currentSectionStoreys() {
      if (!this.currentStorey) return [];
      return this.storeys.filter(
        (storey) => storey.section_id === this.currentStorey.section_id
      );
    },
    itemsToDisplay() {
      return (
        this.instances?.filter(
          (instance) => instance.storey.id == this.currentStorey.id
        ) ?? []
      );
    },
  },
  watch: {
    uuid: {
      handler() {
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        this.init(this.abortController.signal);
      },
      immediate: true,
    },
    visibleSidebars: {
      handler: async function (newSidebar) {
        if (this.frame?.image && !this.canvasHasError) {
          this.handleActiveStateFromSidebars(newSidebar);
        }
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions([
      'TOGGLE_SIDEBAR',
      'OPEN_SIDEBAR',
      'CLOSE_SIDEBAR_BY_PARAMS',
      'axiosWrapped',
    ]),

    handleActiveStateFromSidebars(sidebars) {
      if (!this.frame) return;
      let res =
        sidebars.map((sidebar) => {
          if (this.currentStorey.type == 'group') {
            return sidebar.context * 1;
          }
          return sidebar.subcontext * 1;
        }) ?? [];

      this.frame.layers.forEach((layer) => {
        if (res.includes(layer.action.id)) {
          layer.state.value = 'active';
          if (layer.type != 'polygon' || layer.static) return;
          layer?.tool?.applyLayerStyle(
            layer.shape,
            layer.options.active,
            layer.visible
          );
        } else {
          layer.state.value = 'default';
          if (layer.type != 'polygon' || layer.static) return;
          layer?.tool?.applyLayerStyle(
            layer.shape,
            layer.options.default,
            layer.visible
          );
        }
      });
    },

    handleFrameClick(data) {
      // console.log(data);
      if (!data.layer) return;
      this.TOGGLE_SIDEBAR({
        type: 'details',
        size: 1,
        context: 'instance',
        subcontext: data.layer.action.id,
        payload: {},
        isSingleton: true,
        persist: false,
      });
    },

    changeStorey(storey) {
      this.OPEN_SIDEBAR({
        type: storey.type == 'group' ? 'group' : 'preview',
        size: this.sidebar.size,
        context: storey.type === 'group' ? storey.id : this.sidebar.context,
        subcontext: storey.type === 'group' ? null : storey.id,
        payload: {},
        isSingleton: true,
        persist: false,
      });

      if (storey.type != this.currentStorey.type) {
        this.CLOSE_SIDEBAR_BY_PARAMS({
          type: this.currentStorey.type == 'group' ? 'group' : 'preview',
          context:
            this.currentStorey.type === 'group'
              ? this.currentStorey.id
              : this.sidebar.context,
          subcontext:
            this.currentStorey.type === 'group' ? null : this.currentStorey.id,
        });
      }
    },

    handleLayerMouseOver(data) {
      if (!data) {
        return;
      }
      let { layer } = data;
      if (!layer) return;
      if (layer.static) {
        return;
      }
      this.$refs.canvasContainer.style.cursor = 'pointer';
      if (layer.state.value == 'active') return;
      layer.state.value = 'hover';
      this.highlightedId = layer.action.id;
      if (layer.type != 'polygon' || layer.static) return;
      layer?.tool?.applyLayerStyle(
        layer.shape,
        layer.options.hover,
        layer.visible
      );
    },

    handleLayerMouseLeave(data) {
      this.highlightedId = null;
      if (!data) {
        return;
      }
      let { layer } = data;
      if (!layer) return;
      this.$refs.canvasContainer.style.cursor = 'default';

      if (layer.state.value == 'active') return;
      layer.state.value = 'default';
      if (layer.type != 'polygon' || layer.static) return;
      layer?.tool?.applyLayerStyle(
        layer.shape,
        layer.options.default,
        layer.visible
      );
    },
    handleInstanceMouseOver(instanceId) {
      if (!instanceId) {
        return;
      }

      let layer = this.canvas?.frameManager?.frame.layers.find(
        (layer) => layer.action.id == instanceId
      );
      if (!layer) return;
      if (layer.state.value == 'active') return;
      layer.state.value = 'hover';
      if (layer.type != 'polygon' || layer.static) return;

      layer?.tool?.applyLayerStyle(
        layer.shape,
        layer.options.hover,
        layer.visible
      );
    },
    handleInstanceMouseLeave(instanceId) {
      if (!instanceId) {
        return;
      }
      let layer = this.canvas?.frameManager?.frame.layers.find(
        (layer) => layer.action.id == instanceId
      );
      if (!layer) return;
      if (layer.state.value == 'active') return;
      layer.state.value = 'default';
      if (layer.type != 'polygon' || layer.static) return;
      layer?.tool?.applyLayerStyle(
        layer.shape,
        layer.options.default,
        layer.visible
      );
    },

    interactiveCanvasLoaded(data, render = true) {
      this.canvas = data;
      this.loadingState.canvas = false;
      this.canvasHasError = false;

      if (render) {
        this.canvas.functions.render();
        this.handleActiveStateFromSidebars(this.visibleSidebars);
      }
    },

    interactiveCanvasMounted(data, render = true) {
      data.controls = {
        setTool: function () {},
      };
      this.canvas = data;
      this.registerTools(data);
      this.loadingState.canvas = false;
      this.canvasHasError = false;

      if (render) {
        this.canvas.functions.render();
        this.handleActiveStateFromSidebars(this.visibleSidebars);
      }
    },

    interactiveCanvasError() {
      this.loadingState.canvas = false;
      this.canvasHasError = true;
    },

    registerTools(data) {
      this.canvas.toolManager.registerTool('polygon', new PolygonTool(data));
      this.canvas.toolManager.registerTool('label', new LabelTool(data));
    },

    prepareDisabledLayers() {},

    isDisabledLayer(layer) {
      if (layer.type != 'polygon') return;

      if (!this.interactiveSettings) return;
      if (!layer.action.id) return;

      let instance = this.instances.find(
        (instance) => instance.id === layer.action.id
      );

      if (!instance || instance?.status?.type !== 1) {
        layer.disabled = true;
        layer.options.default.fill.color =
          this.interactiveSettings.disabled_styles.fill.color;
        layer.options.default.stroke.color =
          this.interactiveSettings.disabled_styles.stroke.color;
      }
    },

    async init(signal) {
      this.canvasHasError = false;
      this.loadingState.axios = true;
      this.loadingState.canvas = true;
      let id = this.sidebar.subcontext;
      if (this.currentStorey.type == 'group') {
        id = this.sidebar.context;
      }
      try {
        const response = await this.axiosWrapped({
          url: `public/interactive/sidebar/storey/${id}`,
          signal,
        });
        response.data.data.frame.layers.forEach((layer) =>
          this.isDisabledLayer(layer)
        );

        let labelLayers = [];

        response.data.data.frame.layers.forEach((layer) => {
          if (layer.type === 'polygon' && layer.action.id) {
            let instance = this.instances.find(
              (instance) => instance.id === layer.action.id
            );
            if (!instance) {
              return;
            }

            // Calculate geometric center of polygon
            const points = layer.points;
            if (points && points.length > 0) {
              let area = 0;
              let centerX = 0;
              let centerY = 0;

              // Calculate centroid using polygon area formula
              for (let i = 0; i < points.length; i++) {
                const j = (i + 1) % points.length;
                const cross =
                  points[i][0] * points[j][1] - points[j][0] * points[i][1];
                area += cross;
                centerX += (points[i][0] + points[j][0]) * cross;
                centerY += (points[i][1] + points[j][1]) * cross;
              }

              area = area / 2;
              centerX = centerX / (6 * area);
              centerY = centerY / (6 * area);

              // Ensure positive coordinates
              centerX = Math.abs(centerX);
              centerY = Math.abs(centerY);

              let fontSize = 42;
              let padding = {
                x: 20,
                y: 10,
              };

              let fillColor = '#00000088';

              if (layer?.disabled) {
                fontSize = 24;
                fillColor = '#00000033';
                padding = {
                  x: 12,
                  y: 6,
                };
              }

              // Push label layer with center coordinates
              labelLayers.push({
                id: `label-for-${layer.action.id}`,
                state: {
                  value: 'default',
                  isSelected: false,
                },
                translations: {
                  content: {
                    'lang-0': instance.number,
                  },
                },
                points: [[centerX, centerY]],
                action: layer.action,
                type: 'label',
                visible: 1,
                static: 1,
                options: {
                  text: {
                    basic: {
                      text: {
                        size: fontSize,
                      },
                      stroke: {
                        style: 'solid',
                        radius: 40,
                      },
                      padding: padding,
                      arrow_direction: 'hidden',
                    },
                    default: {
                      fill: {
                        color: fillColor,
                      },
                      text: {
                        color: '#FFFFFFFF',
                      },
                      stroke: {
                        color: '#00000000',
                        width: 0,
                      },
                    },
                  },
                },
              });
            }
          }
        });
        // add labelLayers to  response.data.data.frame.layers
        response.data.data.frame.layers.push(...labelLayers);

        this.interactive = response.data.data;
        this.frame = this.interactive.frame;

        this.loadingState.axios = false;
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error(error);
          this.loadingState.axios = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.storey-interactive .interactive-label {
  // pointer-events: none;
  &-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }
}
</style>
